import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Modal } from '../ui/Modal';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { Select } from '../ui/Select';
import { includesCI } from '../ui/listFilterHelpers';
import { cloneBeamPlanFromOrder, formatOrderCode } from './orderFormHelpers';

function matchesCloneOrderSearch(order, query) {
  const q = String(query || '').trim();
  if (!q) return true;

  const code = String(order.orderCode || '').toLowerCase();
  const idStr = String(order.id ?? '').toLowerCase();
  const ql = q.toLowerCase();
  if (code.includes(ql) || idStr.includes(ql)) return true;

  const digits = ql.replace(/\D/g, '');
  if (digits && idStr.includes(digits)) return true;

  return includesCI(order.designSampleNo, q);
}

/**
 * Clone beam & feeder colour plan from an existing order (same design only).
 */
export function BeamPlanCloneModal({
  open,
  onClose,
  authFetch,
  targetDesignId,
  designs,
  excludeOrderId,
  onApply,
  toast,
}) {
  const [loading, setLoading] = useState(false);
  const [applying, setApplying] = useState(false);
  const [orders, setOrders] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedId, setSelectedId] = useState('');
  const [error, setError] = useState('');

  const sortedOrders = useMemo(
    () =>
      (orders || [])
        .filter((o) => !excludeOrderId || String(o.id) !== String(excludeOrderId))
        .slice()
        .sort((a, b) => {
          const ta = new Date(a.updatedAt || a.createdAt || 0).getTime();
          const tb = new Date(b.updatedAt || b.createdAt || 0).getTime();
          return tb - ta;
        }),
    [orders, excludeOrderId]
  );

  const filteredOrders = useMemo(
    () => sortedOrders.filter((o) => matchesCloneOrderSearch(o, searchQuery)),
    [sortedOrders, searchQuery]
  );

  const orderOptions = useMemo(
    () =>
      filteredOrders.map((o) => ({
        value: String(o.id),
        label: formatOrderCode(o.orderCode || o.id),
      })),
    [filteredOrders]
  );

  const resolvedOrder = useMemo(
    () => filteredOrders.find((o) => String(o.id) === String(selectedId)) || null,
    [filteredOrders, selectedId]
  );

  const clonePreview = useMemo(() => {
    if (!resolvedOrder || !targetDesignId) return null;
    return cloneBeamPlanFromOrder(resolvedOrder, targetDesignId, designs);
  }, [resolvedOrder, targetDesignId, designs]);

  const loadOrders = useCallback(async () => {
    if (!authFetch) return;
    setLoading(true);
    setError('');
    try {
      const data = await authFetch('/api/orders');
      setOrders(Array.isArray(data) ? data : []);
    } catch (e) {
      setError(e.message || 'Unable to load orders');
      setOrders([]);
    } finally {
      setLoading(false);
    }
  }, [authFetch]);

  useEffect(() => {
    if (!open) return;
    setSearchQuery('');
    setSelectedId('');
    setError('');
    loadOrders();
  }, [open, loadOrders]);

  useEffect(() => {
    if (!selectedId) return;
    if (!filteredOrders.some((o) => String(o.id) === String(selectedId))) {
      setSelectedId('');
    }
  }, [filteredOrders, selectedId]);

  const handleApply = async () => {
    if (!resolvedOrder) {
      setError('Select an order to clone from.');
      return;
    }
    const result = cloneBeamPlanFromOrder(resolvedOrder, targetDesignId, designs);
    if (result.error) {
      setError(result.error);
      return;
    }
    setApplying(true);
    setError('');
    try {
      onApply?.(result.beamPlan, resolvedOrder);
      toast?.success?.(
        `Beam & feeder colours copied from ${formatOrderCode(resolvedOrder.orderCode || resolvedOrder.id)}.`
      );
      onClose?.();
    } finally {
      setApplying(false);
    }
  };

  const validationError = clonePreview?.error && resolvedOrder ? clonePreview.error : '';
  const canClone = Boolean(clonePreview?.beamPlan && !clonePreview?.error);

  return (
    <Modal
      open={open}
      onClose={onClose}
      size="md"
      title="Clone from order"
      footer={
        <>
          <Button type="button" variant="outline" onClick={onClose} disabled={applying}>
            Cancel
          </Button>
          <Button type="button" loading={applying} disabled={applying || !canClone} onClick={handleApply}>
            Clone
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        {error ? (
          <p className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800">{error}</p>
        ) : null}
        {validationError && !error ? (
          <p className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900">
            {validationError}
          </p>
        ) : null}

        <Input
          label="Search"
          placeholder="Order ID or design number"
          value={searchQuery}
          onChange={(e) => {
            setSearchQuery(e.target.value);
            setError('');
          }}
          disabled={loading}
        />

        <Select
          label="Order"
          value={selectedId}
          onChange={(e) => {
            setSelectedId(e.target.value);
            setError('');
          }}
          disabled={loading}
          placeholder={
            loading
              ? 'Loading orders…'
              : filteredOrders.length === 0
                ? searchQuery.trim()
                  ? 'No matching orders'
                  : 'Select order'
                : 'Select order'
          }
          options={orderOptions}
        />
      </div>
    </Modal>
  );
}
